import React, { useEffect, useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Search, Plus } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const norm = (s) => (s || "").toLowerCase().replace(/-/g, " ").replace(/\s+/g, " ").trim();
const slug = (s) => s.replace(/\s+/g, "-");

/**
 * Phase 7: searchable skills combobox backed by the full backend taxonomy
 * (curated list + admin-approved community suggestions). Typing something
 * not in the taxonomy offers "Add as a custom skill" — it lands on the
 * profile instantly and is queued for admin review of the global list.
 */
export function SkillsCombobox({ selected, onAdd }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [taxonomy, setTaxonomy] = useState([]);

  useEffect(() => {
    if (open && taxonomy.length === 0) {
      api.skillsTaxonomy().then((d) => setTaxonomy(d.skills || [])).catch(() => {});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- focus management on open/close toggle only

  const selectedNorm = useMemo(() => new Set([...selected].map(norm)), [selected]);
  const nq = norm(q);
  const options = useMemo(() => {
    const pool = taxonomy.filter((t) => !selectedNorm.has(norm(t)));
    if (!nq) return pool.slice(0, 25);
    return pool.filter((t) => norm(t).includes(nq)).slice(0, 25);
  }, [taxonomy, nq, selectedNorm]);
  const exactExists = taxonomy.some((t) => norm(t) === nq);
  const canAddCustom = nq.length >= 2 && nq.length <= 40 && !exactExists && !selectedNorm.has(nq);

  const pick = (s) => {
    onAdd(s);
    setQ("");
  };

  const addCustom = async () => {
    const skill = nq;
    onAdd(skill);
    setQ("");
    try {
      const r = await api.suggestSkill(skill);
      if (r.status === "pending" && !r.already_queued) {
        toast.success(`"${skill}" added to your profile — we'll review it for the global skills list`);
      } else if (r.status === "pending") {
        toast.success(`"${skill}" added — it's already awaiting review for the global list`);
      } else {
        toast.success(`"${skill}" added to your profile`);
      }
    } catch {
      toast.success(`"${skill}" added to your profile`);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="skills-combobox-trigger"
          className="w-full flex items-center gap-2 h-11 px-3.5 rounded-lg border border-neutral-200 bg-white text-sm text-neutral-500 hover:border-forest/40 transition-colors"
        >
          <Search className="w-4 h-4 text-neutral-400" /> Search all skills — or add your own…
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            data-testid="skills-combobox-input"
            value={q}
            onValueChange={setQ}
            placeholder="e.g. community organising"
          />
          <CommandList className="max-h-56">
            {options.map((s) => (
              <CommandItem
                key={s}
                value={s}
                onSelect={() => pick(s)}
                data-testid={`skills-option-${slug(s)}`}
                className="capitalize cursor-pointer"
              >
                {s}
              </CommandItem>
            ))}
            {canAddCustom && (
              <CommandItem
                value={`__custom__${nq}`}
                onSelect={addCustom}
                data-testid="skills-add-custom"
                className="cursor-pointer text-terracotta"
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add "{nq}" as a custom skill
              </CommandItem>
            )}
            {options.length === 0 && !canAddCustom && (
              <div className="py-4 text-center text-sm text-neutral-500">No matching skills.</div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
